import { AssetRequestType, AssetResolver, AssetResolverContext } from "@/core/asset-resolver"
import { LookTable } from "@/core/types"
import type { CatalogWorker } from "@/lib/catalog"
import { type PeerConnection, transfer } from "@/utils/peer"
import type { Renderer } from "@/core/pipeline/renderer"
import { CatalogConnection } from "@/lib/catalog/catalog-connection"
import { LRU } from "@/utils/lru-cache"
import { Sentry } from "@/telemetry/sentry"

const encodeUint32LittleEndian = (value: number) => {
	const buffer = new ArrayBuffer(4)
	new DataView(buffer).setUint32(0, value, true)
	return buffer
}

export class LUTResolver extends AssetResolver {
	private connection: PeerConnection<CatalogWorker>
	private renderer?: Renderer
	private cache = new LRU<string, LookTable>(20)

	public constructor(connection: PeerConnection<CatalogWorker>) {
		super()
		this.connection = connection
	}

	public get type(): AssetRequestType {
		return "lut"
	}

	public prepare(renderer: Renderer) {
		this.renderer = renderer
	}

	public async resolve(id: string, context: AssetResolverContext) {
		const lookTable = await this.loadFromCatalog(id)

		if (!lookTable) {
			console.warn(`[lut-resolver] Requested LUT ${id} cannot be found`)
			Sentry.captureMessage("[lut-resolver] Requested LUT cannot be found", {
				level: "warning",
				extra: { lutId: id },
			})
			return
		}

		context.renderer.assets.setArtefact({ type: this.type, id: id }, lookTable)
	}

	public async get(id: string): Promise<LookTable | null> {
		return this.loadFromCatalog(id)
	}

	public async add(value: LookTable) {
		this.renderer?.assets.setArtefact({ type: this.type, id: value.id }, value)

		await this.saveToCatalog(value)

		this.cache.set(value.id, value)
	}

	private async saveToCatalog(value: Partial<LookTable>) {
		const lutDataBuffer = value.data

		if (!lutDataBuffer || !value.id) {
			return
		}

		// Should include everything except 'data' field
		const lutMetadata = new TextEncoder().encode(
			JSON.stringify(value, (key, value) => {
				if (key === "data") {
					return undefined
				}
				return value as unknown
			}),
		)

		const lutMetadataLength = encodeUint32LittleEndian(lutMetadata.length)

		const data = await new Blob([
			lutMetadataLength,
			lutMetadata,
			lutDataBuffer as unknown as BlobPart,
		]).arrayBuffer()

		await this.connection.proxy?.addObject(value.id, transfer(data, [data]))
	}

	private async loadFromCatalog(id: string): Promise<LookTable | null> {
		const cachedLookTable = this.cache.get(id)

		if (cachedLookTable) {
			return cachedLookTable
		}

		const buffer = await this.connection.proxy?.getObject(id)

		if (!buffer) {
			return null
		}

		const dataView = new DataView(buffer)
		const lutMetadataLength = dataView.getUint32(0, true)

		const lutMetadataString = new TextDecoder().decode(
			new Uint8Array(buffer, 4, lutMetadataLength),
		)
		const lutMetadata = JSON.parse(lutMetadataString) as LookTable

		const lutDataBuffer = buffer.slice(4 + lutMetadataLength)
		const data = new Float32Array(lutDataBuffer)

		const lookTable = {
			...lutMetadata,
			data,
		}

		this.cache.set(id, lookTable)

		return lookTable
	}
}

/**
 * Serialize a LookTable into a binary Blob.
 * Format: [4-byte metadata length (LE)][JSON metadata (excl. data)][Float32Array raw bytes]
 * Consistent with the catalog storage format used by LUTResolver.
 */
export function serializeLookTable(lookTable: LookTable): Blob {
	const lutMetadata = new TextEncoder().encode(
		JSON.stringify(lookTable, (key, value) => {
			if (key === "data") return undefined
			return value as unknown
		}),
	)
	const lutMetadataLength = encodeUint32LittleEndian(lutMetadata.length)
	return new Blob([lutMetadataLength, lutMetadata, lookTable.data as unknown as BlobPart], {
		type: "application/octet-stream",
	})
}

/**
 * Deserialize a binary Blob (or ArrayBuffer) back into a LookTable.
 * Inverse of serializeLookTable.
 */
export function deserializeLookTable(buffer: ArrayBuffer): LookTable {
	const dataView = new DataView(buffer)
	const lutMetadataLength = dataView.getUint32(0, true)
	const lutMetadataString = new TextDecoder().decode(new Uint8Array(buffer, 4, lutMetadataLength))
	const lutMetadata = JSON.parse(lutMetadataString) as Omit<LookTable, "data">
	const lutDataBuffer = buffer.slice(4 + lutMetadataLength)
	const data = new Float32Array(lutDataBuffer)
	return { ...lutMetadata, data }
}

// Main thread (default) LUT resolver
export const MainLUTResolver = new LUTResolver(CatalogConnection)
